/**
 * IdentityDocs — applicant uploads two forms of ID (or one passport).
 *
 * Each upload is bound to a chosen id_type and stored encrypted server-side.
 * After upload, the picked file is rendered as an inline preview:
 *   • images → real JPG/PNG thumbnail
 *   • PDFs   → first-page rasterised via pdf.js
 * Click the thumbnail to view at full size.  An "encryption shimmer" sweep
 * animates across the preview the moment the upload completes.
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  IdentificationCard, IdentificationBadge, Airplane, Baby, NumberSquareNine,
  ShieldCheck, Lock, Trash, CheckCircle, Warning, Eye, FilePdf, ImageSquare,
} from '@phosphor-icons/react';
import { OnboardingPublicAPI } from '../../api/onboardingApi';
import { useFilePreview } from './useFilePreview';
import { PreviewLightbox } from './PreviewLightbox';

const ID_TYPES = [
  { value: 'drivers_license',   label: "Driver's License",     icon: IdentificationBadge, color: '#3B82F6' },
  { value: 'passport',          label: 'Passport',             icon: Airplane,            color: '#10B981' },
  { value: 'birth_certificate', label: 'Birth Certificate',    icon: Baby,                color: '#A855F7' },
  { value: 'ss_card',           label: 'Social Security Card', icon: NumberSquareNine,    color: '#F59E0B' },
];

export function IdentityDocs({ form: _form, set, isLight }) {
  const [slots, setSlots] = useState([
    { type: '', file: null, localFile: null, uploading: false, progress: 0, error: '', justFinished: false },
    { type: '', file: null, localFile: null, uploading: false, progress: 0, error: '', justFinished: false },
  ]);
  const [lightbox, setLightbox] = useState(null);

  const passportFirst = slots[0].type === 'passport' && !!slots[0].file;
  const requireSecond = !passportFirst;

  const setSlot = (idx, patch) => {
    setSlots(s => s.map((sl, i) => i === idx ? { ...sl, ...patch } : sl));
  };

  useEffect(() => {
    const ids = slots.map(s => s.file?.file_id).filter(Boolean);
    set('attachment_ids')(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots]);

  const onPick = (idx) => async (fileLike) => {
    const file = fileLike instanceof File ? fileLike : null;
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setSlot(idx, { error: 'Max 10 MB' }); return; }
    if (!/^(application\/pdf|image\/(jpe?g|png))$/i.test(file.type)) {
      setSlot(idx, { error: 'PDF, JPG, or PNG only' }); return;
    }
    setSlot(idx, { uploading: true, progress: 0, error: '', localFile: file });
    try {
      const r = await OnboardingPublicAPI.uploadFile(file, slots[idx].type || 'drivers_license',
        (p) => setSlot(idx, { progress: p }));
      setSlot(idx, { file: r, uploading: false, justFinished: true });
      // shimmer fades after 1.4s
      setTimeout(() => setSlot(idx, { justFinished: false }), 1400);
    } catch (err) {
      setSlot(idx, { uploading: false, error: err.message || 'Upload failed', localFile: null });
    }
  };

  const removeSlot = (idx) => setSlot(idx, { file: null, localFile: null, type: '', error: '' });

  return (
    <div className="space-y-3" data-testid="identity-docs">
      <div className="flex items-center gap-2">
        <span className="w-7 h-7 rounded-lg inline-flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg, #3B82F6, #A855F7)' }}>
          <ShieldCheck size={13} weight="fill" style={{ color: '#fff' }} />
        </span>
        <div className="flex-1">
          <div className="text-[11px] font-black uppercase tracking-[0.16em]" style={{ color: 'var(--cs-text)' }}>
            Identification documents
          </div>
          <div className="text-[10px]" style={{ color: 'var(--cs-text-muted)' }}>
            Upload {passportFirst ? 'one passport' : 'two forms of ID'} · PDF / JPG / PNG · 10 MB each.
          </div>
        </div>
      </div>

      <IdSlot index={0} required slot={slots[0]} onTypeChange={(t) => setSlot(0, { type: t })}
        onPickFile={onPick(0)} onRemove={() => removeSlot(0)} isLight={isLight}
        onZoom={(meta) => setLightbox(meta)} />

      {requireSecond && (
        <IdSlot index={1} required slot={slots[1]}
          onTypeChange={(t) => setSlot(1, { type: t })}
          onPickFile={onPick(1)} onRemove={() => removeSlot(1)} isLight={isLight}
          firstType={slots[0].type}
          onZoom={(meta) => setLightbox(meta)} />
      )}

      {passportFirst && (
        <div className="text-[11px] flex items-center gap-1.5 rounded-lg px-3 py-2"
          style={{ background: '#10B98114', color: '#10B981', border: '1px solid #10B98144' }}
          data-testid="passport-only-note">
          <CheckCircle size={11} weight="fill" /> Passport satisfies both ID requirements — you&rsquo;re set.
        </div>
      )}

      {lightbox && (
        <PreviewLightbox url={lightbox.url} filename={lightbox.filename}
          onClose={() => setLightbox(null)} />
      )}
    </div>
  );
}

function IdSlot({ index, slot, onTypeChange, onPickFile, onRemove, isLight, firstType, required, onZoom }) {
  const sel = ID_TYPES.find(t => t.value === slot.type);
  const Icon = sel?.icon || IdentificationCard;
  const color = sel?.color || '#3B82F6';
  const previewUrl = useFilePreview(slot.localFile);
  const [hover, setHover] = useState(false);

  /* drag-n-drop magnetic zone */
  const onDragOver = (e) => {
    e.preventDefault();
    if (!hover) setHover(true);
  };
  const onDragLeave = () => setHover(false);
  const onDrop = (e) => {
    e.preventDefault();
    setHover(false);
    const f = e.dataTransfer?.files?.[0];
    if (f && slot.type && !slot.file) onPickFile(f);
  };
  const onPickInput = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) onPickFile(f);
  };

  return (
    <div className="rounded-2xl p-3"
      style={{
        background: isLight ? 'rgba(241,245,249,0.6)' : 'rgba(10,10,14,0.45)',
        border: `1px solid ${isLight ? '#E2E8F0' : 'rgba(255,255,255,0.08)'}`,
      }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-testid={`id-slot-${index}`}>
      <div className="text-[10px] font-black uppercase tracking-wider mb-2 flex items-center justify-between"
        style={{ color: 'var(--cs-text-muted)' }}>
        <span>ID #{index + 1}{required ? ' · required' : ''}</span>
        {slot.file && (
          <span className="inline-flex items-center gap-1" style={{ color: '#10B981' }}>
            <Lock size={9} weight="fill" /> encrypted
          </span>
        )}
      </div>

      {/* Type chooser */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mb-2.5">
        {ID_TYPES.map(t => {
          const Disabled = firstType && firstType === t.value;
          const TIcon = t.icon;
          const active = slot.type === t.value;
          return (
            <button key={t.value} type="button"
              onClick={() => !Disabled && !slot.file && onTypeChange(t.value)}
              disabled={Disabled || slot.uploading || !!slot.file}
              className="px-2 py-2 rounded-lg text-[10px] font-black inline-flex items-center justify-center gap-1.5 transition-all"
              style={{
                background: active ? `${t.color}` : (isLight ? '#FFFFFF' : 'rgba(20,20,28,0.7)'),
                color: active ? '#fff' : (Disabled ? 'var(--cs-text-muted)' : 'var(--cs-text)'),
                border: `1px solid ${active ? 'transparent' : (isLight ? '#E2E8F0' : 'rgba(255,255,255,0.08)')}`,
                opacity: Disabled ? 0.5 : 1,
                cursor: Disabled || slot.file ? 'not-allowed' : 'pointer',
              }}
              data-testid={`id-type-${index}-${t.value}`}>
              <TIcon size={10} weight="fill" />{t.label}
            </button>
          );
        })}
      </div>

      {/* Either dropzone OR uploaded preview */}
      <AnimatePresence mode="wait">
        {!slot.file ? (
          <motion.label key="drop"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              scale: hover && slot.type ? 1.015 : 1,
              borderColor: hover && slot.type ? color : (isLight ? '#CBD5E1' : 'rgba(255,255,255,0.12)'),
            }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 border-dashed ${slot.type ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
            style={{
              background: hover && slot.type
                ? (isLight ? `${color}11` : `${color}22`)
                : (isLight ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.25)'),
              boxShadow: hover && slot.type ? `0 0 0 4px ${color}22` : 'none',
            }}
            data-testid={`id-dropzone-${index}`}>
            <span className="w-8 h-8 rounded-lg inline-flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${color}, ${color}99)` }}>
              <Icon size={14} weight="fill" style={{ color: '#fff' }} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-black truncate" style={{ color: 'var(--cs-text)' }}>
                {slot.uploading
                  ? `Uploading… ${slot.progress}%`
                  : (slot.type
                      ? (hover ? 'Drop your file here' : `Click or drop your ${sel?.label}`)
                      : 'Choose a document type above first')}
              </div>
              <div className="text-[9px]" style={{ color: 'var(--cs-text-muted)' }}>
                PDF, JPG, or PNG · max 10 MB
              </div>
            </div>
            <input type="file" className="hidden"
              accept="application/pdf,image/jpeg,image/png"
              onChange={onPickInput}
              disabled={!slot.type || slot.uploading}
              data-testid={`id-file-input-${index}`} />
          </motion.label>
        ) : (
          <motion.div key="preview"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            data-testid={`id-card-${index}`}>
            <IdCard
              file={slot.file}
              localFile={slot.localFile}
              previewUrl={previewUrl}
              color={color}
              label={sel?.label || 'Document'}
              shimmer={slot.justFinished}
              onZoom={() => onZoom?.({
                url: previewUrl,
                filename: slot.file.filename,
              })}
              onRemove={onRemove}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {slot.uploading && (
        <div className="h-1 rounded-full overflow-hidden mt-2"
          style={{ background: isLight ? '#E2E8F0' : 'rgba(255,255,255,0.08)' }}>
          <div className="h-full transition-all"
            style={{ width: `${slot.progress}%`, background: color }} />
        </div>
      )}
      {slot.error && (
        <div className="text-[10px] flex items-center gap-1 mt-1.5" style={{ color: '#EF4444' }}>
          <Warning size={10} weight="fill" /> {slot.error}
        </div>
      )}
    </div>
  );
}

/**
 * IdCard — uploaded ID rendered inside a faux ID-card frame, with the
 * actual document image (or PDF first page) inside.  Hovering reveals a
 * "click to enlarge" CTA; clicking opens the public lightbox.
 */
function IdCard({ file, previewUrl, color, label, shimmer, onZoom, onRemove }) {
  const isPdf = /pdf/i.test(file?.content_type || '') || /\.pdf$/i.test(file?.filename || '');
  return (
    <div className="relative rounded-2xl overflow-hidden"
      style={{
        background: 'var(--cs-bg-surface-2)',
        border: `1.5px solid ${color}55`,
        boxShadow: `0 8px 28px -12px ${color}77`,
      }}>
      {/* Preview area */}
      <button type="button" onClick={onZoom}
        className="relative w-full aspect-[16/9] block overflow-hidden group"
        style={{
          background: previewUrl
            ? '#0B0B0F'
            : `linear-gradient(135deg, ${color}33, ${color}11)`,
        }}
        data-testid="id-zoom">
        {previewUrl ? (
          <img src={previewUrl} alt={file.filename}
            className="absolute inset-0 w-full h-full object-contain transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            {isPdf
              ? <FilePdf size={42} weight="duotone" style={{ color }} />
              : <ImageSquare size={42} weight="duotone" style={{ color }} />}
          </div>
        )}
        {/* Faux ID-card chrome */}
        <div className="absolute top-2 left-2 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-[0.22em]"
          style={{
            background: 'rgba(0,0,0,0.62)',
            color: '#fff',
            backdropFilter: 'blur(4px)',
          }}>
          {label}
        </div>
        <div className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider"
          style={{ background: 'rgba(16,185,129,0.85)', color: '#0B0B0F' }}>
          <Lock size={9} weight="fill" /> verified
        </div>
        {/* Holographic gold corner — purely cosmetic ID-card vibe */}
        <div className="absolute -top-3 -right-3 w-20 h-20 rounded-full pointer-events-none"
          style={{
            background: `radial-gradient(circle, ${color}88, transparent 60%)`,
            mixBlendMode: 'screen',
            opacity: 0.6,
          }} />
        {/* Hover hint */}
        <div className="absolute inset-0 flex items-end justify-end p-2 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded"
            style={{ background: '#fff', color: '#0B0B0F' }}>
            <Eye size={10} weight="bold" /> Click to enlarge
          </span>
        </div>
        {/* Encryption shimmer overlay (gold sweep) */}
        <AnimatePresence>
          {shimmer && (
            <motion.div
              key="shimmer"
              initial={{ x: '-110%', opacity: 0.0 }}
              animate={{ x: '110%', opacity: 0.55 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.1, ease: [0.2, 0.9, 0.3, 1] }}
              className="absolute inset-y-0 w-1/2 pointer-events-none"
              style={{
                background: 'linear-gradient(110deg, transparent 0%, rgba(245,158,11,0.55) 45%, rgba(255,255,255,0.7) 50%, rgba(245,158,11,0.55) 55%, transparent 100%)',
                filter: 'blur(2px)',
                mixBlendMode: 'screen',
              }} />
          )}
        </AnimatePresence>
      </button>

      {/* Footer row */}
      <div className="flex items-center gap-2 px-3 py-2">
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-black truncate" style={{ color: 'var(--cs-text)' }}>
            {file.filename}
          </div>
          <div className="text-[10px] inline-flex items-center gap-1" style={{ color: 'var(--cs-text-muted)' }}>
            {(file.size_bytes / 1024).toFixed(0)} KB · {label}
          </div>
        </div>
        <CheckCircle size={13} weight="fill" style={{ color: '#10B981' }} />
        <button type="button" onClick={onRemove}
          className="p-1.5 rounded-md hover:opacity-100 opacity-60"
          style={{ color: '#EF4444' }}
          data-testid="id-remove-btn">
          <Trash size={11} weight="bold" />
        </button>
      </div>
    </div>
  );
}
